/**
 * Run trend — how one scenario (outline + persona binding) moves across its
 * completed runs: each run's verdict and the problems it found, oldest first.
 *
 * Built only from data every run carries (`walkthroughs.verdict` and the
 * per-run `problemCounts`), never from the unpopulated scenario scores.
 */

import {
  composeWalkthroughTone,
  getPrimaryVerdict,
  verdictLabel,
  type Tone,
  type VerdictRating,
} from "@/components/experience/vocabulary";

/** Hinders / missing / incorrect findings of one run; `open` = not yet addressed. */
export type ProblemCounts = { found: number; open: number };

export interface TrendRunInput {
  id: string;
  status: string;
  verdict?: unknown;
  problemCounts?: ProblemCounts | null;
  completedAt?: string | Date | null;
  startedAt?: string | Date | null;
  createdAt: string | Date;
}

export interface TrendPoint {
  id: string;
  runNumber: number;
  rating: VerdictRating | null;
  tone: Tone;
  /** Problems this run found; null when the count is unknown. */
  problems: number | null;
  /** Of those, how many are still open today. */
  openProblems: number | null;
}

/**
 * When a run happened — start, else completion, else creation. Start orders
 * runs the way they were walked; completion is set by the CLI and has been
 * re-stamped by report reassembly, so it is only a fallback.
 */
export function runTime(
  run: Pick<TrendRunInput, "completedAt" | "startedAt" | "createdAt">,
): number {
  return new Date(run.startedAt ?? run.completedAt ?? run.createdAt).getTime();
}

/**
 * "Run #N" for every run of one scenario: its place in chronological
 * order, counting every run (failed and running ones too). The one
 * numbering the scenario's run list and the run page share.
 */
export function numberRuns(
  runs: ReadonlyArray<
    Pick<TrendRunInput, "id" | "completedAt" | "startedAt" | "createdAt">
  >,
): Map<string, number> {
  const chronological = [...runs].sort((a, b) => runTime(a) - runTime(b));
  return new Map(chronological.map((run, index) => [run.id, index + 1]));
}

/**
 * Completed runs in chronological order. Run numbers count every run
 * (failed and running ones too). A caller that already numbers its rows
 * passes that numbering, so a mark reads the same "Run N" as the row
 * beneath it, in the same order.
 */
export function buildRunTrend(
  runs: readonly TrendRunInput[],
  runNumbers?: ReadonlyMap<string, number>,
): TrendPoint[] {
  const chronological = [...runs].sort((a, b) => runTime(a) - runTime(b));
  const points: TrendPoint[] = [];
  chronological.forEach((run, index) => {
    if (run.status !== "completed") return;
    const rating = getPrimaryVerdict(
      Array.isArray(run.verdict)
        ? (run.verdict as Array<{ rating: string }>)
        : null,
    );
    points.push({
      id: run.id,
      runNumber: runNumbers?.get(run.id) ?? index + 1,
      rating,
      tone: composeWalkthroughTone(run.status, rating),
      problems: run.problemCounts?.found ?? null,
      openProblems: run.problemCounts?.open ?? null,
    });
  });
  // A caller's numbering is its order; keep the marks in that order.
  return runNumbers ? points.sort((a, b) => a.runNumber - b.runNumber) : points;
}

function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? "" : "s"}`;
}

/** Screen-reader and tooltip text for one mark. */
export function describeTrendPoint(point: TrendPoint): string {
  const parts = [
    point.rating ? `verdict ${verdictLabel(point.rating)}` : "no verdict",
  ];
  if (point.problems !== null) {
    parts.push(`${plural(point.problems, "problem")} found`);
    if (point.problems > 0 && point.openProblems !== null) {
      parts.push(`${point.openProblems} still open`);
    }
  }
  return `Run ${point.runNumber}: ${parts.join(", ")}`;
}

function sameOutcome(a: TrendPoint, b: TrendPoint): boolean {
  return a.rating === b.rating && a.problems === b.problems;
}

function stateOf(point: TrendPoint): string[] {
  const parts: string[] = [];
  if (point.rating) parts.push(`verdict ${verdictLabel(point.rating)}`);
  if (point.problems !== null) {
    parts.push(`${plural(point.problems, "problem")} found`);
  }
  return parts;
}

/**
 * One line that says what moved. Compares the latest run with the first
 * (with the previous one when there are only two runs), and flags a stall
 * when the latest run matches the one before it. The verdict word is always
 * spelled out, so the marks' colour is never the only carrier.
 *
 *   "Since first run: verdict No → Partial, problems found 6 → 2"
 *   "No change since last run: verdict Partial, 4 problems found"
 */
export function describeRunTrend(points: readonly TrendPoint[]): string | null {
  // One run has no trend; its row already says everything.
  if (points.length < 2) return null;
  const latest = points[points.length - 1];
  const first = points[0];
  const previous = points[points.length - 2];
  const base = points.length === 2 ? "last run" : "first run";

  if (sameOutcome(first, latest)) {
    const state = stateOf(latest);
    return `No change since ${base}${state.length ? `: ${state.join(", ")}` : ""}`;
  }

  const changes: string[] = [];
  if (first.rating !== latest.rating && latest.rating) {
    changes.push(
      first.rating
        ? `verdict ${verdictLabel(first.rating)} → ${verdictLabel(latest.rating)}`
        : `verdict ${verdictLabel(latest.rating)}`,
    );
  } else if (latest.rating) {
    changes.push(`verdict still ${verdictLabel(latest.rating)}`);
  }
  if (
    first.problems !== null &&
    latest.problems !== null &&
    first.problems !== latest.problems
  ) {
    changes.push(`problems found ${first.problems} → ${latest.problems}`);
  } else if (latest.problems !== null) {
    changes.push(`still ${plural(latest.problems, "problem")} found`);
  }

  const summary = `Since ${base}: ${changes.join(", ")}`;
  return points.length > 2 && sameOutcome(previous, latest)
    ? `${summary} · no change since last run`
    : summary;
}
